import { useEffect } from "react";
import {
  ensurePlausibleLoaded,
  enablePlausible,
  shutdownPlausible,
} from "@/lib/plausible";
import { getConsent, CONSENT_KEY } from "@/lib/analyticsConsent";
import { isProductionAnalyticsHost } from "@/lib/analyticsHost";

/**
 * Boots Plausible Analytics on production hosts. Plausible is cookieless
 * and anonymous by design, so it loads without prior consent. Users who
 * explicitly Reject in the cookie banner / /cookies page get the
 * `plausible_ignore` opt-out flag set, which suppresses event sending.
 *
 * Re-runs on cross-tab `storage` events and same-tab
 * `excerptus:consent-changed` events so a Reject/Accept takes effect
 * without a page reload.
 *
 * Plausible's default script auto-tracks SPA `pushState` route changes,
 * so we do NOT fire manual pageview calls here.
 */
export const usePlausible = (): void => {
  useEffect(() => {
    if (!isProductionAnalyticsHost()) return;

    const apply = () => {
      if (getConsent() === "denied") {
        shutdownPlausible();
      } else {
        enablePlausible();
        ensurePlausibleLoaded();
      }
    };

    apply();

    const onStorage = (e: StorageEvent) => {
      if (e.key === CONSENT_KEY) apply();
    };
    const onConsentChanged = () => apply();

    window.addEventListener("storage", onStorage);
    window.addEventListener("excerptus:consent-changed", onConsentChanged as EventListener);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("excerptus:consent-changed", onConsentChanged as EventListener);
    };
  }, []);
};
